import { Badge, Button, cn, Select } from '@aibi/ui';
import { RotateCcw } from 'lucide-react';
import type { DiscoveredColumn, DiscoveredTable, MappingAggregation } from '../services/admin-api';
import {
  AGGREGATION_LABELS,
  assignmentKey,
  assignmentOf,
  ROLE_LABELS,
  rolesFor,
  summarize,
  withRole,
  type Assignment,
  type Assignments,
} from './column-mapping';

export interface ColumnMappingProps {
  readonly tables: readonly DiscoveredTable[];
  readonly assignments: Assignments;
  readonly onChange: (key: string, assignment: Assignment) => void;
  readonly onReset: () => void;
}

const range = (column: DiscoveredColumn): string => {
  if (column.redacted) return 'Oculto (dato sensible)';
  const show = (value: DiscoveredColumn['min']) =>
    typeof value === 'number' ? value.toLocaleString('es') : String(value);
  if (column.min === null && column.max === null) return '';
  if (column.min === column.max || column.max === null) return show(column.min);
  return `${show(column.min)} – ${show(column.max)}`;
};

/**
 * Asignacion explicita de cada columna: dimension (agrupar y filtrar), medida (se
 * agrega), ambas o no usar. Parte de la sugerencia del servidor y muestra en vivo a
 * donde va cada columna.
 */
export function ColumnMapping({ tables, assignments, onChange, onReset }: ColumnMappingProps) {
  const summary = summarize(tables, assignments);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground">
          <li>
            <span className="font-medium text-foreground">Dimension</span>: para agrupar y filtrar
            (fecha, sucursal, producto, periodo).
          </li>
          <li>
            <span className="font-medium text-foreground">Medida</span>: valor que se suma, promedia
            o cuenta (importe, cantidad).
          </li>
          <li>
            <span className="font-medium text-foreground">Ambas</span>: por ejemplo, Folio para
            filtrar y su conteo distinto como numero de tickets.
          </li>
        </ul>
        <Button size="sm" variant="ghost" onClick={onReset}>
          <RotateCcw /> Restablecer sugerencias
        </Button>
      </div>

      {tables.map((table) => (
        <div key={table.id} className="flex flex-col gap-1">
          {tables.length > 1 && <p className="text-xs font-semibold">{table.name}</p>}
          <div className="overflow-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 font-medium">Columna</th>
                  <th className="px-2 py-1.5 font-medium">Tipo</th>
                  <th className="px-2 py-1.5 font-medium">Valores</th>
                  <th className="px-2 py-1.5 font-medium">Uso</th>
                  <th className="px-2 py-1.5 font-medium">Agregacion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {table.columns.map((column) => {
                  const key = assignmentKey(table.id, column.name);
                  const current = assignmentOf(assignments, table.id, column.name);
                  const measure = current.role === 'measure' || current.role === 'both';
                  return (
                    <tr key={column.name} className={cn(current.role === 'none' && 'opacity-60')}>
                      <td className="px-2 py-1.5 font-medium">{column.name}</td>
                      <td className="px-2 py-1.5 text-muted-foreground">{column.logicalType}</td>
                      <td className="max-w-48 truncate px-2 py-1.5 text-muted-foreground">
                        {range(column)}
                      </td>
                      <td className="px-2 py-1.5">
                        <div
                          role="radiogroup"
                          aria-label={`Uso de ${column.name}`}
                          className="inline-flex overflow-hidden rounded-md border border-border"
                        >
                          {rolesFor(column).map((role) => (
                            <button
                              key={role}
                              type="button"
                              role="radio"
                              aria-checked={current.role === role}
                              className={cn(
                                'whitespace-nowrap border-l border-border px-2 py-1 first:border-l-0',
                                current.role === role
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-accent',
                              )}
                              onClick={() => {
                                onChange(key, withRole(current, role, column));
                              }}
                            >
                              {ROLE_LABELS[role]}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        {measure ? (
                          <Select
                            aria-label={`Agregacion de ${column.name}`}
                            className="h-7 w-36 text-xs"
                            value={current.aggregation ?? ''}
                            onChange={(event) => {
                              onChange(key, {
                                role: current.role,
                                aggregation: event.target.value as MappingAggregation,
                              });
                            }}
                          >
                            {column.aggregations.map((aggregation) => (
                              <option key={aggregation} value={aggregation}>
                                {AGGREGATION_LABELS[aggregation]}
                              </option>
                            ))}
                          </Select>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="grid gap-2 sm:grid-cols-2" aria-live="polite">
        <SummaryBox title="Dimensiones" items={summary.dimensions} />
        <SummaryBox title="Medidas" items={summary.measures} />
      </div>
      {summary.unused > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {summary.unused} columnas sin usar: quedan ocultas en el modelo.
        </p>
      )}
    </div>
  );
}

function SummaryBox({
  title,
  items,
}: {
  readonly title: string;
  readonly items: readonly string[];
}) {
  return (
    <div className="rounded-lg border border-border p-2">
      <p className="mb-1 flex items-center gap-2 text-xs font-semibold">
        {title} <Badge variant="secondary">{items.length}</Badge>
      </p>
      <p className="text-xs text-muted-foreground">
        {items.length === 0 ? 'Ninguna' : items.join(', ')}
      </p>
    </div>
  );
}
